import './ClassList.less';

import { DeleteFilled, DragOutlined } from '@ant-design/icons';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  Button,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useCarrierUpdate,
  useClassCreate,
  useClassRemove,
  useClassUpdate,
  useClassVehicleMapUpdate,
} from '../../hooks/apiHooks';
import { getAlphabet } from '../../library/util';
import { UserRole } from '../../models/User';
import Container from '../common/Container';
import EditButton from '../common/EditButton';
import ListHeader from '../common/ListHeader';
import PermissionsToggle from '../common/PermissionsToggle';
import ClassModal from '../modals/ClassModal';
import DraggableVehicleChip from './DraggableVehicleChip';
import DroppableRateClass from './DroppableRateClass';
import DroppableUnassignedContainer from './DroppableUnassignedContainer';

/*
interface IProps {
  id: string;
  carrier: Carrier;
  userRole?: UserRole;
  vehicles: Vehicle[];
  classVehicleMap: ClassVehicles;
}
*/

const ClassList = ({ carrier, classVehicleMap, id, userRole, vehicles }) => {
  const createMutation = useClassCreate();
  const updateMutation = useClassUpdate();
  const removeMutation = useClassRemove();
  const carrierUpdateMutation = useCarrierUpdate();
  const updateCarrierVehicleMapMutation = useClassVehicleMapUpdate();

  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [deletableClass, setDeletableClass] = useState();
  const [showAddModal, setShowAddModal] = useState(false);
  const [classNames, setClassNames] = useState({});
  const [formValues, setFormValues] = useState({});
  const [activeId, setActiveId] = useState(null);
  const originalFormData = useRef({});
  const originalClassNames = useRef({});

  const classes = useMemo(() => carrier.classes || [], [carrier]);

  const initializeClassNames = useCallback(() => {
    const names = {};
    classes.forEach((c) => {
      names[c.id] = c.name;
    });
    return names;
  }, [classes]);

  // find classes with changed names
  const changedClassNames = useMemo(() => {
    const changes = [];
    classes.forEach((c) => {
      if (classNames[c.id] && classNames[c.id] !== c.name) {
        changes.push({ ...c, name: classNames[c.id] });
      }
    });
    return changes;
  }, [classNames, classes]);

  // checking if any class name is empty
  const hasEmptyClassName = useMemo(() => {
    return classes.some((c) => !classNames[c.id]?.trim());
  }, [classNames, classes]);

  const onAdd = useCallback(
    (classification) => {
      classification.carrierId = carrier.id;
      createMutation.mutate(classification);
    },
    [carrier.id, createMutation]
  );

  const onDelete = useCallback(() => {
    if (deletableClass) {
      removeMutation.mutate(deletableClass);
    }
  }, [deletableClass, removeMutation]);

  const onSubmit = useCallback(
    async (classVehicles) => {
      // prevent save if any class name is empty
      if (hasEmptyClassName) {
        return;
      }

      // first, update any changed class names
      for (const changedClass of changedClassNames) {
        await updateMutation.mutateAsync(changedClass);
      }

      // then update vehicle assignments
      updateCarrierVehicleMapMutation.mutate(classVehicles);
    },
    [updateCarrierVehicleMapMutation, changedClassNames, updateMutation, hasEmptyClassName]
  );

  const handleSave = useCallback(() => {
    if (hasEmptyClassName) {
      return;
    }
    form.submit();
  }, [form, hasEmptyClassName]);

  const handleEdit = useCallback(() => {
    // store orig values before editing
    originalFormData.current = form.getFieldsValue();
    originalClassNames.current = { ...classNames };
    setFormValues(originalFormData.current);
    setIsEditing(true);
  }, [form, classNames]);

  const handleCancel = useCallback(() => {
    // revert form values
    form.setFieldsValue(originalFormData.current);
    setFormValues(originalFormData.current);
    // revert class names
    setClassNames(originalClassNames.current);
    setDeletableClass(undefined);
    setIsEditing(false);
  }, [form]);

  const handleClassNameChange = useCallback((classId, newName) => {
    setClassNames((prev) => ({
      ...prev,
      [classId]: newName,
    }));
  }, []);

  const onTogglePermission = useCallback(
    (permission) => {
      const newCarrier = {
        ...carrier,
        classPermission: permission,
      };
      carrierUpdateMutation.mutate(newCarrier);
    },
    [carrier, carrierUpdateMutation]
  );

  useEffect(() => {
    form.setFieldsValue(classVehicleMap);
    setFormValues(classVehicleMap || {});
  }, [classVehicleMap, form]);

  // initialize class names when classes change
  useEffect(() => {
    setClassNames(initializeClassNames());
  }, [initializeClassNames]);

  useEffect(() => {
    if (!isEditing) {
      setDeletableClass(undefined);
    }
  }, [isEditing]);

  useEffect(() => {
    if (createMutation.isSuccess) {
      setShowAddModal(false);
    }
  }, [createMutation.isSuccess]);

  // turn off editing mode when vehicle map is successfully saved
  useEffect(() => {
    if (updateCarrierVehicleMapMutation.isSuccess) {
      setIsEditing(false);
    }
  }, [updateCarrierVehicleMapMutation.isSuccess]);

  const isSaving = updateCarrierVehicleMapMutation.isLoading || updateMutation.isLoading;

  const leftColumnClasses = useMemo(() => {
    return classes.filter((_, index) => index % 2 === 0);
  }, [classes]);

  const rightColumnClasses = useMemo(() => {
    return classes.filter((_, index) => index % 2 === 1);
  }, [classes]);

  const currentVehicleMap = useMemo(() => {
    if (isEditing) {
      return formValues;
    }
    // filter classVehicleMap to only include classes that are in carrier.classes
    const filteredMap = {};
    const classIds = new Set(classes.map((c) => String(c.id)));
    Object.keys(classVehicleMap || {}).forEach((classId) => {
      if (classIds.has(String(classId))) {
        filteredMap[classId] = classVehicleMap[classId];
      }
    });
    return filteredMap;
  }, [isEditing, formValues, classVehicleMap, classes]);

  const assignedVehicleIds = useMemo(() => {
    const allAssigned = Object.values(currentVehicleMap || {}).flat();
    return new Set(allAssigned);
  }, [currentVehicleMap]);

  const unassignedVehicles = useMemo(() => {
    return vehicles.filter((v) => !assignedVehicleIds.has(v.id));
  }, [vehicles, assignedVehicleIds]);

  const getAssignedVehiclesForClass = useCallback(
    (classId) => {
      const vehicleIds = formValues[classId] || [];
      return vehicles.filter((v) => vehicleIds.includes(v.id));
    },
    [vehicles, formValues]
  );

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || !isEditing) {
        return;
      }

      const activeData = active.data.current;
      const overData = over.data.current;

      if (!activeData || !overData) {
        return;
      }

      // dragging vehicle type from unassigned to a rate class
      if (activeData.type === 'vehicle' && overData.type === 'rate-class') {
        const vehicleId = activeData.vehicle.id;
        const targetClassId = overData.classId;

        // get current assignments
        const currentValues = form.getFieldsValue();
        const currentClassVehicles = currentValues[targetClassId] || [];

        // Don't add if already assigned
        if (currentClassVehicles.includes(vehicleId)) {
          return;
        }

        // Remove from previous class if assigned to another class
        const updatedValues = { ...currentValues };
        Object.keys(updatedValues).forEach((classId) => {
          if (classId !== targetClassId && Array.isArray(updatedValues[classId])) {
            updatedValues[classId] = updatedValues[classId].filter((id) => id !== vehicleId);
          }
        });

        // add to target class
        updatedValues[targetClassId] = [...currentClassVehicles, vehicleId];

        form.setFieldsValue(updatedValues);
        setFormValues(updatedValues);
      }

      // Moving a vehicle type back to unassigned
      if (activeData.type === 'vehicle' && overData.type === 'unassigned') {
        const vehicleId = activeData.vehicle.id;

        const currentValues = form.getFieldsValue();
        const updatedValues = { ...currentValues };

        Object.keys(updatedValues).forEach((classId) => {
          if (Array.isArray(updatedValues[classId])) {
            updatedValues[classId] = updatedValues[classId].filter((id) => id !== vehicleId);
          }
        });

        form.setFieldsValue(updatedValues);
        setFormValues(updatedValues);
      }
    },
    [form, isEditing]
  );

  const handleRemoveVehicle = useCallback(
    (classId, vehicleId) => {
      const currentValues = form.getFieldsValue();
      const currentClassVehicles = (currentValues[classId] || []).filter((id) => id !== vehicleId);
      const updatedValues = {
        ...currentValues,
        [classId]: currentClassVehicles,
      };
      form.setFieldsValue(updatedValues);
      setFormValues(updatedValues);
    },
    [form]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  return (
    <Container id={id}>
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <ListHeader>
          <Space>
            <span>Vehicle Classifications</span>
            {userRole === UserRole.SUPER_ADMIN && (
              <PermissionsToggle
                defaultValue={carrier.classPermission}
                submitting={carrierUpdateMutation.isLoading}
                onChange={onTogglePermission}
              />
            )}
          </Space>

          <Space>
            {isEditing ? (
              <>
                <EditButton
                  disabled={hasEmptyClassName}
                  loading={isSaving}
                  onClick={handleSave}
                >
                  Save
                </EditButton>
                <Button onClick={handleCancel}>
                  Cancel
                </Button>
              </>
            ) : (
              <EditButton onClick={handleEdit}>
                Edit
              </EditButton>
            )}
            <Button
              ghost
              style={{ width: 120 }}
              type="primary"
              onClick={() => setShowAddModal(true)}
            >
              Add Class
            </Button>
          </Space>
        </ListHeader>

        <DndContext sensors={sensors} onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
          <div className="class-list__grid">
            {/* unassigned vehicle types */}
            <div className="class-list__unassigned-column">
              <div className="class-list__unassigned-header">
                <Typography.Text strong>Unassigned Vehicle Types</Typography.Text>
              </div>
              <DroppableUnassignedContainer isEditing={isEditing}>
                {unassignedVehicles.length === 0 ? (
                  <Typography.Text
                    style={{ display: 'block', padding: '20px', textAlign: 'center' }}
                    type="secondary"
                  >
                    No unassigned vehicles
                  </Typography.Text>
                ) : (
                  <Space wrap size={[8, 8]}>
                    {unassignedVehicles.map((vehicle) => (
                      <DraggableVehicleChip key={vehicle.id} isEditing={isEditing} vehicle={vehicle} />
                    ))}
                  </Space>
                )}
              </DroppableUnassignedContainer>
            </div>

            {/* left column rate classes */}
            <div className="class-list__rate-classes-column">
              {leftColumnClasses.map((classification, index) => {
                const actualIndex = index * 2;
                return (
                  <div key={classification.id} className="class-list__rate-class-container">
                    <div className="class-list__rate-class-header">
                      <Space>
                        {isEditing ? (
                          <Input
                            placeholder={`Class ${getAlphabet(actualIndex)}`}
                            size="middle"
                            status={!classNames[classification.id]?.trim() ? 'error' : undefined}
                            style={{ width: 155 }}
                            value={classNames[classification.id] || ''}
                            onChange={(e) => handleClassNameChange(classification.id, e.target.value)}
                          />
                        ) : (
                          <span>
                            {classification.name}{' '}
                            {classification.name !== `Class ${getAlphabet(actualIndex)}` && (
                              <Typography.Text type="secondary">
                                (Class {getAlphabet(actualIndex)})
                              </Typography.Text>
                            )}
                          </span>
                        )}
                        {isEditing && (
                          <Popconfirm
                            okButtonProps={{ loading: removeMutation.isLoading }}
                            open={deletableClass?.id === classification.id}
                            title="Are you sure to delete this Rate Class?"
                            onCancel={() => setDeletableClass(undefined)}
                            onConfirm={onDelete}
                          >
                            <Tooltip title="Delete Rate Class">
                              <DeleteFilled
                                style={{ color: '#ff4d4f', cursor: 'pointer' }}
                                onClick={() => setDeletableClass(classification)}
                              />
                            </Tooltip>
                          </Popconfirm>
                        )}
                      </Space>
                    </div>
                    <Form.Item
                      key={classification.id}
                      noStyle
                      name={classification.id}
                    >
                      <DroppableRateClass
                        assignedVehicles={getAssignedVehiclesForClass(classification.id)}
                        classification={classification}
                        isEditing={isEditing}
                        vehicles={vehicles}
                        onRemoveVehicle={handleRemoveVehicle}
                      />
                      {/* hidden Select to bind form data */}
                      <Select
                        className="class-list__vehicle-select"
                        disabled={!isEditing}
                        mode="multiple"
                        placeholder="Vehicle Types"
                        style={{ display: 'none' }}
                      >
                        {vehicles.map((vehicle) => (
                          <Select.Option key={vehicle.id} value={vehicle.id}>
                            {vehicle.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                );
              })}
            </div>

            {/* right column rate classes */}
            <div className="class-list__rate-classes-column">
              {rightColumnClasses.map((classification, index) => {
                const actualIndex = index * 2 + 1;
                return (
                  <div key={classification.id} className="class-list__rate-class-container">
                    <div className="class-list__rate-class-header">
                      <Space>
                        {isEditing ? (
                          <Input
                            placeholder={`Class ${getAlphabet(actualIndex)}`}
                            size="middle"
                            status={!classNames[classification.id]?.trim() ? 'error' : undefined}
                            style={{ width: 155 }}
                            value={classNames[classification.id] || ''}
                            onChange={(e) => handleClassNameChange(classification.id, e.target.value)}
                          />
                        ) : (
                          <span>
                            {classification.name}{' '}
                            {classification.name !== `Class ${getAlphabet(actualIndex)}` && (
                              <Typography.Text type="secondary">
                                (Class {getAlphabet(actualIndex)})
                              </Typography.Text>
                            )}
                          </span>
                        )}
                        {isEditing && (
                          <Popconfirm
                            okButtonProps={{ loading: removeMutation.isLoading }}
                            open={deletableClass?.id === classification.id}
                            title="Are you sure to delete this Rate Class?"
                            onCancel={() => setDeletableClass(undefined)}
                            onConfirm={onDelete}
                          >
                            <Tooltip title="Delete Rate Class">
                              <DeleteFilled
                                style={{ color: '#ff4d4f', cursor: 'pointer' }}
                                onClick={() => setDeletableClass(classification)}
                              />
                            </Tooltip>
                          </Popconfirm>
                        )}
                      </Space>
                    </div>
                    <Form.Item
                      key={classification.id}
                      noStyle
                      name={classification.id}
                    >
                      <DroppableRateClass
                        assignedVehicles={getAssignedVehiclesForClass(classification.id)}
                        classification={classification}
                        isEditing={isEditing}
                        vehicles={vehicles}
                        onRemoveVehicle={handleRemoveVehicle}
                      />
                      {/* hidden Select to bind form data */}
                      <Select
                        className="class-list__vehicle-select"
                        disabled={!isEditing}
                        mode="multiple"
                        placeholder="Vehicle Types"
                        style={{ display: 'none' }}
                      >
                        {vehicles.map((vehicle) => (
                          <Select.Option key={vehicle.id} value={vehicle.id}>
                            {vehicle.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                );
              })}
            </div>
          </div>
          <DragOverlay>
            {activeId ? (() => {
              const vehicleId = activeId.toString().replace('vehicle-', '');
              const draggedVehicle = vehicles.find((v) => String(v.id) === vehicleId);
              return draggedVehicle ? (
                <Tag className="class-list__vehicle-chip">
                  <Space size={4}>
                    {isEditing && <DragOutlined />}
                    {draggedVehicle.name}
                  </Space>
                </Tag>
              ) : null;
            })() : null}
          </DragOverlay>
        </DndContext>
      </Form>
      {showAddModal && (
        <ClassModal
          classes={classes}
          error={createMutation.error}
          submitting={createMutation.isLoading}
          onClose={() => setShowAddModal(false)}
          onSubmit={onAdd}
        />
      )}
    </Container>
  );
};

export default ClassList;
